<template>
  <div class="animated fadeIn pvp-usage-view">
    <div class="usage-container">
      <b-card no-body class="border-0 shadow-sm mb-3 bg-dark text-white usage-toolbar-card">
        <b-card-body class="p-4">
          <div class="usage-toolbar">
            <div class="usage-title">
              <div class="usage-title-icon">
                <i class="fa fa-bar-chart"></i>
              </div>
              <div>
                <h4 class="mb-1">Usage Stats</h4>
                <div class="text-muted small">Monthly PvP Pokemon usage by most used.</div>
              </div>
            </div>

            <div class="usage-controls">
              <div class="control-block">
                <div class="control-label">Server</div>
                <div class="btn-group btn-group-sm">
                  <button
                    v-for="server in activeServers"
                    :key="server"
                    type="button"
                    class="btn"
                    :class="activeServer === server ? 'btn-info active' : 'btn-outline-secondary'"
                    @click="switchServer(server)"
                  >
                    {{ capitalize(server) }}
                  </button>
                </div>
              </div>

              <div class="control-block month-control">
                <div class="control-label">Month</div>
                <b-form-select
                  v-model="selectedMonthKey"
                  :options="monthOptions"
                  size="sm"
                  @change="expandedKey = null"
                ></b-form-select>
              </div>

              <div class="control-block search-control">
                <div class="control-label">Search</div>
                <b-input-group size="sm" class="search-box-group">
                  <b-input-group-prepend class="search-box-prepend">
                    <i class="fa fa-search"></i>
                  </b-input-group-prepend>
                  <b-form-input
                    v-model="quickFilter"
                    placeholder="Pokemon name..."
                    class="search-box-input"
                  />
                  <b-input-group-append v-if="quickFilter">
                    <b-button variant="outline-secondary" class="search-clear" @click="quickFilter = ''">
                      <i class="fa fa-times small"></i>
                    </b-button>
                  </b-input-group-append>
                </b-input-group>
              </div>

              <b-button variant="outline-info" size="sm" :disabled="loading" @click="refresh">
                <i class="fa fa-refresh" :class="{ 'fa-spin': loading }"></i>
              </b-button>
            </div>
          </div>
        </b-card-body>
      </b-card>

      <b-row class="mb-3">
        <b-col cols="12" md="4" class="mb-2">
          <div class="meta-tile">
            <div class="meta-label">Total matches</div>
            <div class="meta-value">{{ selectedMonth ? selectedMonth.total_matches : 0 }}</div>
          </div>
        </b-col>
        <b-col cols="12" md="4" class="mb-2">
          <div class="meta-tile">
            <div class="meta-label">Last update</div>
            <div class="meta-value meta-date">{{ selectedMonth ? formatDate(selectedMonth.last_updated) : '-' }}</div>
          </div>
        </b-col>
        <b-col cols="12" md="4" class="mb-2">
          <div class="meta-tile">
            <div class="meta-label">{{ selectedMonth && selectedMonth.is_current ? 'Next update' : 'Archive status' }}</div>
            <div class="meta-value meta-date">
              <span v-if="selectedMonth && selectedMonth.is_current">{{ nextUpdateCountdown }}</span>
              <span v-else>Stored snapshot</span>
            </div>
          </div>
        </b-col>
      </b-row>

      <b-card no-body class="border-0 shadow-sm bg-dark text-white usage-table-card">
        <div v-if="loading" class="loading-overlay">
          <b-spinner label="Loading..." variant="info"></b-spinner>
          <div class="text-muted mt-2">Loading usage stats...</div>
        </div>

        <div v-if="!loading && !filteredPokemon.length" class="empty-state">
          <i class="fa fa-bar-chart fa-3x mb-3"></i>
          <div class="h5">No usage data found.</div>
        </div>

        <div v-else ref="tableWrapper" class="table-responsive usage-table-wrapper">
          <table class="table table-dark table-hover mb-0 usage-table">
            <thead>
              <tr>
                <th class="rank-col">Rank</th>
                <th>Pokemon</th>
                <th class="text-right">Count</th>
                <th class="text-right">Percentage</th>
                <th class="text-right">Win rate</th>
              </tr>
            </thead>
            <tbody v-for="pokemon in filteredPokemon" :key="rowKey(pokemon)">
              <tr :class="{ 'row-expanded': isExpanded(pokemon) }">
                <td class="rank-col">#{{ displayRank(pokemon) }}</td>
                <td>
                  <div class="pokemon-cell">
                    <button type="button" class="pokemon-link" @click="togglePokemon(pokemon)">
                      <span class="pokemon-icon">
                        <img
                          v-if="getPokemonSpriteUrl(pokemon)"
                          :src="getPokemonSpriteUrl(pokemon)"
                          :alt="pokemon.Name"
                          @error="handleIconError"
                        />
                        <i class="fa fa-question" :class="{ 'fallback-visible': !getPokemonSpriteUrl(pokemon) }" aria-hidden="true"></i>
                      </span>
                      <span>{{ pokemon.Name || 'Unknown' }}</span>
                      <i class="fa ml-2" :class="isExpanded(pokemon) ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
                    </button>
                    <b-button
                      size="sm"
                      variant="outline-info"
                      class="evolution-button"
                      :aria-label="`Show ${pokemon.Name || 'Pokemon'} trend`"
                      v-b-tooltip.hover
                      title="Show monthly trend"
                      @click.stop="openEvolution(pokemon)"
                    >
                      <i class="fa fa-line-chart"></i>
                    </b-button>
                  </div>
                </td>
                <td class="text-right font-weight-bold usage-count">{{ statCount(pokemon) }}</td>
                <td class="text-right usage-percent">{{ formatPercent(pokemon.Percentage) }}</td>
                <td class="text-right">
                  <span class="win-rate" :class="winRateClass(pokemon.WinRate)">
                    {{ formatPercent(pokemon.WinRate) }}
                  </span>
                </td>
              </tr>
              <tr v-if="isExpanded(pokemon)" class="detail-row">
                <td colspan="5">
                  <div class="detail-panel">
                    <div class="detail-header">
                      <span class="detail-pokemon-icon">
                        <img
                          v-if="getPokemonSpriteUrl(pokemon)"
                          :src="getPokemonSpriteUrl(pokemon)"
                          :alt="pokemon.Name"
                          @error="handleIconError"
                        />
                        <i class="fa fa-question" :class="{ 'fallback-visible': !getPokemonSpriteUrl(pokemon) }" aria-hidden="true"></i>
                      </span>
                      <div>
                        <div class="detail-name">{{ pokemon.Name }}</div>
                        <div class="detail-subtitle">
                          <span class="detail-metric detail-metric-strong" v-b-tooltip.hover title="Count">{{ statCount(pokemon) }} uses</span>,
                          <span class="detail-metric detail-metric-strong" v-b-tooltip.hover title="Usage">{{ formatPercent(pokemon.Percentage) }} usage</span>,
                          <span class="detail-metric win-rate" :class="winRateClass(pokemon.WinRate)" v-b-tooltip.hover title="Win Rate">{{ formatPercent(pokemon.WinRate) }} win rate</span>,
                          <span class="detail-metric" v-b-tooltip.hover title="Opener usage">{{ formatPercent(pokemon['Opener%']) }} opener</span>
                        </div>
                      </div>
                    </div>

                    <b-row>
                      <b-col cols="12" lg="4" class="mb-3">
                        <div class="stat-block">
                          <div class="stat-block-title"><i class="fa fa-leaf mr-2"></i>Natures</div>
                          <div v-if="limitedRows(pokemon.Natures).length">
                            <div v-for="row in limitedRows(pokemon.Natures)" :key="`nature-${row.Name}`" class="stat-line">
                              <span class="stat-name">{{ row.Name || 'None' }}</span>
                              <span class="stat-values">
                                <span class="usage-value" v-b-tooltip.hover title="Usage">{{ formatPercent(row['Use%']) }}</span>
                                <span class="stat-separator">/</span>
                                <span class="win-rate" :class="winRateClass(row['Win%'])" v-b-tooltip.hover title="Win Rate">{{ formatPercent(row['Win%']) }}</span>
                              </span>
                            </div>
                          </div>
                          <div v-else class="stat-empty">No data</div>
                        </div>
                      </b-col>
                      <b-col cols="12" lg="4" class="mb-3">
                        <div class="stat-block">
                          <div class="stat-block-title"><i class="fa fa-bolt mr-2"></i>Abilities</div>
                          <div v-if="limitedRows(pokemon.Abilities).length">
                            <div v-for="row in limitedRows(pokemon.Abilities)" :key="`ability-${row.Name}`" class="stat-line">
                              <span class="stat-name">{{ row.Name || 'None' }}</span>
                              <span class="stat-values">
                                <span class="usage-value" v-b-tooltip.hover title="Usage">{{ formatPercent(row['Use%']) }}</span>
                                <span class="stat-separator">/</span>
                                <span class="win-rate" :class="winRateClass(row['Win%'])" v-b-tooltip.hover title="Win Rate">{{ formatPercent(row['Win%']) }}</span>
                              </span>
                            </div>
                          </div>
                          <div v-else class="stat-empty">No data</div>
                        </div>
                      </b-col>
                      <b-col cols="12" lg="4" class="mb-3">
                        <div class="stat-block">
                          <div class="stat-block-title"><i class="fa fa-crosshairs mr-2"></i>Moves</div>
                          <div v-if="limitedRows(pokemon.Moves).length">
                            <div v-for="row in limitedRows(pokemon.Moves)" :key="`move-${row.Name}`" class="stat-line">
                              <span class="stat-name">{{ row.Name || 'None' }}</span>
                              <span class="stat-values">
                                <span class="usage-value" v-b-tooltip.hover title="Usage">{{ formatPercent(row['Use%']) }}</span>
                                <span class="stat-separator">/</span>
                                <span class="win-rate" :class="winRateClass(row['Win%'])" v-b-tooltip.hover title="Win Rate">{{ formatPercent(row['Win%']) }}</span>
                              </span>
                            </div>
                          </div>
                          <div v-else class="stat-empty">No data</div>
                        </div>
                      </b-col>
                      <b-col cols="12" lg="4" class="mb-3">
                        <div class="stat-block">
                          <div class="stat-block-title"><i class="fa fa-gift mr-2"></i>Held Items</div>
                          <div v-if="limitedRows(pokemon.HeldItems).length">
                            <div v-for="item in limitedRows(pokemon.HeldItems)" :key="`item-${item.ID}-${item.Name}`" class="stat-line">
                              <span class="stat-name with-icon">
                                <span v-if="item.Name && item.Name !== 'None'" class="item-icon">
                                  <img :src="getItemSpriteUrl(item)" :alt="item.Name" @error="handleIconError" />
                                  <i class="fa fa-question" aria-hidden="true"></i>
                                </span>
                                {{ item.Name || 'None' }}
                              </span>
                              <span class="stat-values">
                                <span class="usage-value" v-b-tooltip.hover title="Usage">{{ formatPercent(item['Use%']) }}</span>
                                <span class="stat-separator">/</span>
                                <span class="win-rate" :class="winRateClass(item['Win%'])" v-b-tooltip.hover title="Win Rate">{{ formatPercent(item['Win%']) }}</span>
                              </span>
                            </div>
                          </div>
                          <div v-else class="stat-empty">No data</div>
                        </div>
                      </b-col>
                      <b-col cols="12" lg="4" class="mb-3">
                        <div class="stat-block">
                          <div class="stat-block-title"><i class="fa fa-users mr-2"></i>Teammates</div>
                          <div v-if="limitedRows(pokemon.Teammates).length">
                            <div v-for="mate in limitedRows(pokemon.Teammates)" :key="`mate-${mate.DexID}-${mate.Name}`" class="stat-line">
                              <span class="stat-name with-icon">
                                <span class="pokemon-icon mini">
                                  <img
                                    v-if="getPokemonSpriteUrl(mate)"
                                    :src="getPokemonSpriteUrl(mate)"
                                    :alt="mate.Name"
                                    @error="handleIconError"
                                  />
                                  <i class="fa fa-question" :class="{ 'fallback-visible': !getPokemonSpriteUrl(mate) }" aria-hidden="true"></i>
                                </span>
                                {{ mate.Name }}
                              </span>
                              <span class="stat-values">
                                <span class="usage-value" v-b-tooltip.hover title="Usage">{{ formatPercent(mate['Use%']) }}</span>
                                <span class="stat-separator">/</span>
                                <span class="win-rate" :class="winRateClass(mate['Win%'])" v-b-tooltip.hover title="Win Rate">{{ formatPercent(mate['Win%']) }}</span>
                              </span>
                            </div>
                          </div>
                          <div v-else class="stat-empty">No data</div>
                        </div>
                      </b-col>
                      <b-col cols="12" lg="4" class="mb-3">
                        <div class="stat-block">
                          <div class="stat-block-title"><i class="fa fa-list mr-2"></i>Move Sets</div>
                          <div v-if="limitedRows(pokemon.MoveSets).length">
                            <div v-for="(set, setIndex) in limitedRows(pokemon.MoveSets)" :key="`set-${setIndex}`" class="moveset-line">
                              <div class="moveset-moves">
                                <span v-for="move in set.MoveSet" :key="`${setIndex}-${move}`" class="move-chip">{{ move }}</span>
                              </div>
                              <div class="moveset-stats">
                                <span class="usage-value" v-b-tooltip.hover title="Usage">{{ formatPercent(set['Use%']) }}</span>
                                <span class="stat-separator">/</span>
                                <span class="win-rate" :class="winRateClass(set['Win%'])" v-b-tooltip.hover title="Win Rate">{{ formatPercent(set['Win%']) }}</span>
                              </div>
                            </div>
                          </div>
                          <div v-else class="stat-empty">No data</div>
                        </div>
                      </b-col>
                    </b-row>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </b-card>

      <b-modal
        v-model="evolutionModalVisible"
        size="xl"
        hide-footer
        body-bg-variant="dark"
        body-text-variant="light"
        header-bg-variant="dark"
        header-text-variant="light"
        modal-class="pvp-evolution-modal"
        :title="evolutionTitle"
      >
        <div v-if="!evolutionRows.length" class="empty-state evolution-empty">
          <i class="fa fa-line-chart fa-2x mb-3"></i>
          <div>No monthly trend data found for this Pokemon.</div>
        </div>
        <div v-else class="table-responsive evolution-table-wrapper">
          <table class="table table-dark mb-0 evolution-table">
            <thead>
              <tr>
                <th>Month</th>
                <th class="text-right">Rank</th>
                <th class="text-right">Count</th>
                <th class="text-right">Percentage</th>
                <th class="text-right">Win Rate</th>
              </tr>
            </thead>
            <tbody v-for="row in evolutionRows" :key="`evolution-${row.month.key}`">
              <tr>
                <td class="evolution-month">{{ formatShortMonth(row.month) }}</td>
                <td class="text-right evolution-metric">
                  <span>{{ formatRank(row.rank) }}</span>
                  <span class="trend-indicator" :class="trendClass(row.rankTrend)" v-b-tooltip.hover :title="trendTitle(row.rankTrend, 'Rank')">
                    <i class="fa" :class="trendIconClass(row.rankTrend)"></i>
                  </span>
                </td>
                <td class="text-right evolution-metric">
                  <span>{{ formatInteger(row.count) }}</span>
                  <span class="trend-indicator" :class="trendClass(row.countTrend)" v-b-tooltip.hover :title="trendTitle(row.countTrend, 'Count')">
                    <i class="fa" :class="trendIconClass(row.countTrend)"></i>
                  </span>
                </td>
                <td class="text-right evolution-metric">
                  <span class="usage-value" v-b-tooltip.hover title="Usage">{{ formatPercent(row.percentage) }}</span>
                  <span class="trend-indicator" :class="trendClass(row.percentageTrend)" v-b-tooltip.hover :title="trendTitle(row.percentageTrend, 'Usage')">
                    <i class="fa" :class="trendIconClass(row.percentageTrend)"></i>
                  </span>
                </td>
                <td class="text-right evolution-metric">
                  <span class="win-rate" :class="winRateClass(row.winRate)" v-b-tooltip.hover title="Win Rate">{{ formatPercent(row.winRate) }}</span>
                  <span class="trend-indicator" :class="trendClass(row.winRateTrend)" v-b-tooltip.hover :title="trendTitle(row.winRateTrend, 'Win Rate')">
                    <i class="fa" :class="trendIconClass(row.winRateTrend)"></i>
                  </span>
                </td>
              </tr>
              <tr v-if="row.anomalies.length" class="evolution-anomaly-row">
                <td colspan="5">
                  <div class="evolution-anomaly-list">
                    <div v-for="anomaly in row.anomalies" :key="`${row.month.key}-${anomaly.category}-${anomaly.name}`" class="evolution-anomaly">
                      <span class="anomaly-category">{{ anomaly.category }}</span>
                      <span class="anomaly-name">{{ anomaly.name }}</span>
                      <span class="anomaly-values">
                        <span class="usage-value" v-b-tooltip.hover title="Previous usage">{{ formatPercent(anomaly.previousUse) }}</span>
                        <span class="stat-separator">/</span>
                        <span class="win-rate" :class="winRateClass(anomaly.previousWin)" v-b-tooltip.hover title="Previous Win Rate">{{ formatPercent(anomaly.previousWin) }}</span>
                        <span class="anomaly-arrow">-&gt;</span>
                        <span class="usage-value" v-b-tooltip.hover title="Current usage">{{ formatPercent(anomaly.currentUse) }}</span>
                        <span class="stat-separator">/</span>
                        <span class="win-rate" :class="winRateClass(anomaly.currentWin)" v-b-tooltip.hover title="Current Win Rate">{{ formatPercent(anomaly.currentWin) }}</span>
                      </span>
                    </div>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </b-modal>
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import { mapGetters } from 'vuex'
import Servers from '@/_servers'
import Monsters from '@/views/pokemon/_monsters'

export default {
  name: 'PvpUsageStats',
  data() {
    return {
      activeServer: 'gold',
      selectedMonthKey: '',
      quickFilter: '',
      expandedKey: null,
      evolutionModalVisible: false,
      evolutionPokemon: null,
      resetScrollOnNextData: false,
      loading: false,
      tick: Date.now(),
      timer: null,
      servers: Servers,
      monsters: Object.freeze(Monsters)
    }
  },
  computed: {
    ...mapGetters(['pvpUsageStats', 'pvpUsageStatsReceivedAt']),
    activeServers() {
      return Object.keys(this.servers).filter(server => !this.servers[server].readonly && !this.servers[server].test)
    },
    monthOptions() {
      return this.months.map(month => ({
        value: month.key,
        text: month.is_current ? `${month.label} (current)` : month.label
      }))
    },
    months() {
      return this.pvpUsageStats && this.pvpUsageStats.months ? this.pvpUsageStats.months : []
    },
    selectedMonth() {
      return this.months.find(month => month.key === this.selectedMonthKey) || this.months[0] || null
    },
    sortedPokemon() {
      const rows = this.selectedMonth && this.selectedMonth.pokemon ? this.selectedMonth.pokemon : []
      return rows.slice().sort((a, b) => {
        const countDiff = this.statCount(b) - this.statCount(a)
        if (countDiff !== 0) return countDiff
        return String(a.Name || '').localeCompare(String(b.Name || ''))
      })
    },
    filteredPokemon() {
      const query = this.quickFilter.trim().toLowerCase()
      if (!query) return this.sortedPokemon
      return this.sortedPokemon.filter(row => String(row.Name || '').toLowerCase().includes(query))
    },
    rankByKey() {
      const ranks = {}
      this.sortedPokemon.forEach((pokemon, index) => {
        ranks[this.rowKey(pokemon)] = index + 1
      })
      return ranks
    },
    evolutionTitle() {
      const name = this.evolutionPokemon && this.evolutionPokemon.Name ? this.evolutionPokemon.Name : 'Pokemon'
      return `${name} Monthly Trend`
    },
    evolutionRows() {
      if (!this.evolutionPokemon) return []
      return this.months.map((month, index) => {
        const pokemon = this.findPokemonInMonth(month, this.evolutionPokemon)
        const previousMonth = this.months[index + 1] || null
        const previousPokemon = previousMonth ? this.findPokemonInMonth(previousMonth, this.evolutionPokemon) : null
        const rank = pokemon ? this.rankForPokemonInMonth(month, pokemon) : null
        const previousRank = previousPokemon && previousMonth ? this.rankForPokemonInMonth(previousMonth, previousPokemon) : null
        const count = pokemon ? this.statCount(pokemon) : 0
        const previousCount = previousPokemon ? this.statCount(previousPokemon) : null
        const percentage = pokemon ? Number(pokemon.Percentage || 0) : 0
        const previousPercentage = previousPokemon ? Number(previousPokemon.Percentage || 0) : null
        const winRate = pokemon ? Number(pokemon.WinRate || 0) : 0
        const previousWinRate = previousPokemon ? Number(previousPokemon.WinRate || 0) : null

        return {
          month,
          pokemon,
          rank,
          count,
          percentage,
          winRate,
          rankTrend: this.rankTrend(rank, previousRank),
          countTrend: this.valueTrend(count, previousCount),
          percentageTrend: this.valueTrend(percentage, previousPercentage),
          winRateTrend: this.valueTrend(winRate, previousWinRate),
          anomalies: this.findMonthlyAnomalies(previousPokemon, pokemon)
        }
      }).filter(row => row.pokemon || row.count > 0 || row.anomalies.length)
    },
    nextUpdateCountdown() {
      const tick = this.tick
      if (tick < 0) return '-'
      if (!this.selectedMonth || !this.selectedMonth.next_update) return '-'
      const diff = moment.utc(this.selectedMonth.next_update).valueOf() - Date.now()
      if (diff <= 0) return 'Refresh due'
      const totalSeconds = Math.floor(diff / 1000)
      const hours = Math.floor(totalSeconds / 3600)
      const minutes = Math.floor((totalSeconds % 3600) / 60)
      const seconds = totalSeconds % 60
      return `${this.pad(hours)}:${this.pad(minutes)}:${this.pad(seconds)}`
    }
  },
  watch: {
    pvpUsageStatsReceivedAt() {
      this.loading = false
      if (!this.months.some(month => month.key === this.selectedMonthKey)) {
        this.selectedMonthKey = this.months.length ? this.months[0].key : ''
      }
      this.expandedKey = null
      if (this.resetScrollOnNextData) {
        this.resetScrollOnNextData = false
        this.$nextTick(this.resetTableScroll)
      }
    }
  },
  created() {
    if (!this.activeServers.includes(this.activeServer) && this.activeServers.length) {
      this.activeServer = this.activeServers[0]
    }
    this.refresh()
  },
  mounted() {
    this.timer = setInterval(() => {
      this.tick = Date.now()
    }, 1000)
  },
  beforeDestroy() {
    if (this.timer) {
      clearInterval(this.timer)
    }
  },
  methods: {
    refresh() {
      if (!this.activeServer) return
      this.loading = true
      this.emit('getPvpUsageStats', { server: this.activeServer })
    },
    switchServer(server) {
      if (server === this.activeServer) return
      this.activeServer = server
      this.selectedMonthKey = ''
      this.expandedKey = null
      this.resetScrollOnNextData = true
      this.resetTableScroll()
      this.refresh()
    },
    capitalize(value) {
      return String(value || '').charAt(0).toUpperCase() + String(value || '').slice(1)
    },
    pad(value) {
      return String(value).padStart(2, '0')
    },
    rowKey(pokemon) {
      return `${pokemon.DexID || 0}-${pokemon.Name || 'unknown'}`
    },
    isExpanded(pokemon) {
      return this.expandedKey === this.rowKey(pokemon)
    },
    togglePokemon(pokemon) {
      const key = this.rowKey(pokemon)
      this.expandedKey = this.expandedKey === key ? null : key
    },
    openEvolution(pokemon) {
      this.evolutionPokemon = pokemon
      this.evolutionModalVisible = true
    },
    displayRank(pokemon) {
      return this.rankByKey[this.rowKey(pokemon)] || '-'
    },
    statCount(pokemon) {
      return Number(pokemon.Count || pokemon.Matches || 0)
    },
    formatInteger(value) {
      return Number(value || 0).toLocaleString()
    },
    formatPercent(value) {
      const numeric = Number(value || 0)
      return `${numeric.toFixed(2)}%`
    },
    formatRank(rank) {
      return rank ? `#${rank}` : '-'
    },
    formatDate(value) {
      if (!value) return '-'
      return moment.utc(value).local().format('YYYY-MM-DD HH:mm:ss')
    },
    formatShortMonth(month) {
      if (!month || !month.key) return '-'
      return month.label || moment.utc(`${month.key}-01`).format('MMMM YYYY')
    },
    winRateClass(value) {
      const numeric = Number(value || 0)
      if (numeric > 50) return 'win-rate-high'
      if (numeric < 50) return 'win-rate-low'
      return ''
    },
    limitedRows(rows) {
      return (rows || []).slice(0, 8)
    },
    sortedPokemonForMonth(month) {
      const rows = month && month.pokemon ? month.pokemon : []
      return rows.slice().sort((a, b) => {
        const countDiff = this.statCount(b) - this.statCount(a)
        if (countDiff !== 0) return countDiff
        return String(a.Name || '').localeCompare(String(b.Name || ''))
      })
    },
    findPokemonInMonth(month, reference) {
      const rows = month && month.pokemon ? month.pokemon : []
      const referenceDex = this.getPokemonDex(reference)
      const referenceName = String(reference && reference.Name ? reference.Name : '').toLowerCase()
      return rows.find(row => Number(row.DexID || 0) === referenceDex && String(row.Name || '').toLowerCase() === referenceName) ||
        (referenceDex ? rows.find(row => Number(row.DexID || 0) === referenceDex) : null) ||
        rows.find(row => String(row.Name || '').toLowerCase() === referenceName) ||
        null
    },
    rankForPokemonInMonth(month, reference) {
      const pokemon = this.findPokemonInMonth(month, reference)
      if (!pokemon) return null
      const key = this.rowKey(pokemon)
      const index = this.sortedPokemonForMonth(month).findIndex(row => this.rowKey(row) === key)
      return index >= 0 ? index + 1 : null
    },
    valueTrend(current, previous) {
      if (previous === null || previous === undefined || current === null || current === undefined) return 'none'
      if (current > previous) return 'up'
      if (current < previous) return 'down'
      return 'same'
    },
    rankTrend(currentRank, previousRank) {
      if (!previousRank || !currentRank) return 'none'
      if (currentRank < previousRank) return 'up'
      if (currentRank > previousRank) return 'down'
      return 'same'
    },
    trendClass(trend) {
      return {
        up: 'trend-up',
        down: 'trend-down',
        same: 'trend-same',
        none: 'trend-none'
      }[trend] || 'trend-none'
    },
    trendIconClass(trend) {
      return {
        up: 'fa-arrow-up',
        down: 'fa-arrow-down',
        same: 'fa-minus',
        none: 'fa-minus'
      }[trend] || 'fa-minus'
    },
    trendTitle(trend, metric) {
      if (trend === 'up') return `${metric} increased compared to the previous month`
      if (trend === 'down') return `${metric} decreased compared to the previous month`
      if (trend === 'same') return `${metric} is unchanged compared to the previous month`
      return `No previous month data for ${metric}`
    },
    findMonthlyAnomalies(previousPokemon, currentPokemon) {
      if (!previousPokemon || !currentPokemon) return []
      const categories = [
        { key: 'Moves', label: 'Move' },
        { key: 'HeldItems', label: 'Item' },
        { key: 'Abilities', label: 'Ability' }
      ]
      const anomalies = []

      categories.forEach(category => {
        const previousRows = this.rateRowsByName(previousPokemon[category.key])
        const currentRows = this.rateRowsByName(currentPokemon[category.key])
        const names = Array.from(new Set([...Object.keys(previousRows), ...Object.keys(currentRows)]))
        names.forEach(name => {
          const previous = previousRows[name] || { use: 0, win: 0 }
          const current = currentRows[name] || { use: 0, win: 0 }
          const useDiff = Math.abs(current.use - previous.use)
          const winDiff = Math.abs(current.win - previous.win)
          if (useDiff < 20 && winDiff < 20) return
          anomalies.push({
            category: category.label,
            name,
            previousUse: previous.use,
            previousWin: previous.win,
            currentUse: current.use,
            currentWin: current.win,
            diff: Math.max(useDiff, winDiff)
          })
        })
      })

      return anomalies.sort((a, b) => {
        if (b.diff !== a.diff) return b.diff - a.diff
        if (a.category !== b.category) return a.category.localeCompare(b.category)
        return a.name.localeCompare(b.name)
      }).slice(0, 6)
    },
    rateRowsByName(rows) {
      return (rows || []).reduce((result, row) => {
        const name = row && row.Name ? String(row.Name) : 'None'
        result[name] = {
          use: Number(row && row['Use%'] ? row['Use%'] : 0),
          win: Number(row && row['Win%'] ? row['Win%'] : 0)
        }
        return result
      }, {})
    },
    resetTableScroll() {
      this.$nextTick(() => {
        if (this.$refs.tableWrapper) {
          this.$refs.tableWrapper.scrollTop = 0
        }
        if (typeof window !== 'undefined' && window.scrollTo) {
          window.scrollTo(0, 0)
        }
      })
    },
    getPokemonDex(value) {
      if (typeof value === 'number') return value
      if (value && value.DexID) return Number(value.DexID)
      const name = value && value.Name ? String(value.Name).toLowerCase() : ''
      if (!name) return 0
      const found = this.monsters.find(monster => String(monster.Name || '').toLowerCase() === name)
      return found ? Number(found.ID) : 0
    },
    getPokemonSpriteUrl(value) {
      const dex = this.getPokemonDex(value)
      if (!dex || Number.isNaN(dex)) return ''
      const name = value && value.Name ? String(value.Name) : ''
      if (!this.hasExactBaseSpriteName(dex, name)) return ''
      return `https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/${dex}.png`
    },
    hasExactBaseSpriteName(dex, name) {
      if (!name) return false
      const current = this.monsters.find(monster => Number(monster.ID) === Number(dex))
      if (!current || String(current.Name || '') !== name) return false
      return !this.hasKnownFormName(name)
    },
    hasKnownFormName(name) {
      return /\b(Alola|Alolan|Galarian|Hisuian|Paldean|Mega|Primal|Origin|Therian|Incarnate|Forme?|Mode|Sky|Midnight|Dusk|Dawn|Ultra|Gigantamax|Gmax|Fan|Wash|Heat|Frost|Mow|Attack|Defense|Speed|Complete|Ash|Black|White)\b/i.test(String(name || ''))
    },
    getItemSpriteUrl(item) {
      const name = item && item.Name ? item.Name : ''
      if (!name || name === 'None') return ''
      const slug = String(name)
        .toLowerCase()
        .replace(/'/g, '')
        .replace(/\./g, '')
        .replace(/:/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .trim()
      return `https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/items/${slug}.png`
    },
    handleIconError(event) {
      if (!event || !event.target) return
      const target = event.target
      const fallbackId = target.dataset ? target.dataset.fallbackId : ''
      if (fallbackId) {
        target.src = this.getPokemonSpriteUrl(Number(fallbackId))
        target.dataset.fallbackId = ''
        return
      }
      target.style.display = 'none'
    }
  }
}
</script>

<style scoped>
.usage-container {
  max-width: 1500px;
  margin: 0 auto;
}

.usage-toolbar-card,
.usage-table-card {
  border-radius: 8px;
}

.usage-toolbar {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}

.usage-title {
  display: flex;
  align-items: center;
  gap: 12px;
}

.usage-title-icon {
  width: 44px;
  height: 44px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(32, 168, 216, 0.15);
  color: #8bdcf4;
  font-size: 20px;
}

.usage-controls {
  display: flex;
  align-items: flex-end;
  gap: 10px;
  flex-wrap: wrap;
}

.control-block {
  min-width: 140px;
}

.month-control {
  min-width: 190px;
}

.search-control {
  min-width: 240px;
}

.control-label,
.meta-label {
  font-size: 0.68rem;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.5);
  font-weight: 700;
  margin-bottom: 4px;
}

.search-box-group {
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: #12161b;
}

.search-box-prepend {
  background: #12161b;
  border: 0;
  color: rgba(255, 255, 255, 0.55);
  padding: 0 12px;
  display: flex;
  align-items: center;
}

.search-box-input {
  background: #12161b;
  border: 0;
  color: #fff;
  font-weight: 600;
}

.search-clear {
  border: 0;
  border-left: 1px solid rgba(255, 255, 255, 0.08);
  background: #12161b;
  color: rgba(255, 255, 255, 0.6);
}

.meta-tile {
  background: #23282c;
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 8px;
  padding: 14px 16px;
  min-height: 76px;
}

.meta-value {
  font-size: 1.35rem;
  color: #fff;
  font-weight: 700;
}

.meta-date {
  font-size: 1rem;
  line-height: 1.5;
}

.usage-table-card {
  position: relative;
  min-height: 280px;
  overflow: hidden;
}

.usage-table-wrapper {
  max-height: calc(100vh - 260px);
  min-height: 280px;
  overflow-y: auto;
}

.usage-table thead th {
  position: sticky;
  top: 0;
  z-index: 2;
  background: #1f2327;
  border-top: 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.68);
  font-size: 0.72rem;
  text-transform: uppercase;
}

.usage-table tbody + tbody {
  border-top: 0;
}

.usage-table td {
  vertical-align: middle;
  border-top-color: rgba(255, 255, 255, 0.05);
}

.rank-col {
  width: 90px;
  color: rgba(255, 255, 255, 0.55);
  font-weight: 700;
}

.pokemon-cell {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.pokemon-link {
  border: 0;
  background: transparent;
  color: #fff;
  padding: 0;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-weight: 700;
}

.pokemon-link:focus,
.pokemon-link:active {
  outline: 0;
  box-shadow: none;
}

.pokemon-link:hover {
  color: #8bdcf4;
}

.evolution-button {
  flex: 0 0 auto;
  padding: 0.15rem 0.45rem;
}

.usage-count,
.usage-percent {
  color: #fff;
}

.pokemon-icon,
.item-icon,
.detail-pokemon-icon {
  position: relative;
  width: 34px;
  height: 34px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.06);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
}

.pokemon-icon.mini {
  width: 24px;
  height: 24px;
  border-radius: 6px;
}

.pokemon-icon img,
.item-icon img,
.detail-pokemon-icon img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  position: relative;
  z-index: 1;
}

.pokemon-icon i,
.item-icon i,
.detail-pokemon-icon i {
  display: none;
  color: rgba(255, 255, 255, 0.45);
}

.pokemon-icon img[style*="display: none"] + i,
.item-icon img[style*="display: none"] + i,
.detail-pokemon-icon img[style*="display: none"] + i {
  display: inline-block;
}

.pokemon-icon i.fallback-visible,
.detail-pokemon-icon i.fallback-visible {
  display: inline-block;
}

.win-rate {
  font-weight: 700;
  color: #fff;
}

.win-rate-high {
  color: #4dbd74;
}

.win-rate-low {
  color: #f86c6b;
}

.row-expanded {
  background: rgba(32, 168, 216, 0.08);
}

.detail-row td {
  padding: 0;
  background: #181c20;
}

.detail-panel {
  padding: 18px;
  border-top: 1px solid rgba(32, 168, 216, 0.25);
}

.detail-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
}

.detail-pokemon-icon {
  width: 56px;
  height: 56px;
}

.detail-name {
  font-size: 1.2rem;
  font-weight: 800;
  color: #fff;
}

.detail-subtitle {
  color: rgba(255, 255, 255, 0.58);
  font-size: 0.86rem;
}

.detail-metric-strong {
  color: #fff;
}

.stat-block {
  background: #23282c;
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 8px;
  padding: 12px;
  height: 100%;
}

.stat-block-title {
  color: #8bdcf4;
  font-weight: 800;
  font-size: 0.78rem;
  text-transform: uppercase;
  margin-bottom: 10px;
}

.stat-line {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 6px 0;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
}

.stat-line:first-child {
  border-top: 0;
}

.stat-name {
  color: #fff;
  font-weight: 600;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.stat-name.with-icon {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.stat-values {
  color: rgba(255, 255, 255, 0.65);
  font-size: 0.78rem;
  white-space: nowrap;
}

.usage-value {
  color: rgba(255, 255, 255, 0.78);
  font-weight: 700;
}

.stat-separator {
  color: rgba(255, 255, 255, 0.42);
  margin: 0 4px;
}

.stat-empty {
  color: rgba(255, 255, 255, 0.45);
  font-size: 0.85rem;
}

.moveset-line {
  padding: 8px 0;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
}

.moveset-line:first-child {
  border-top: 0;
}

.moveset-moves {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  margin-bottom: 4px;
}

.move-chip {
  border-radius: 999px;
  background: rgba(32, 168, 216, 0.15);
  color: #ccefff;
  padding: 2px 8px;
  font-size: 0.72rem;
  font-weight: 700;
}

.moveset-stats {
  color: rgba(255, 255, 255, 0.62);
  font-size: 0.78rem;
}

.evolution-empty {
  min-height: 180px;
}

.evolution-table-wrapper {
  max-height: 70vh;
  overflow-y: auto;
}

.evolution-table th {
  border-top: 0;
  color: rgba(255, 255, 255, 0.68);
  font-size: 0.72rem;
  text-transform: uppercase;
}

.evolution-table td {
  border-top-color: rgba(255, 255, 255, 0.06);
  vertical-align: middle;
}

.evolution-month {
  color: #fff;
  font-weight: 800;
  white-space: nowrap;
}

.evolution-metric {
  color: #fff;
  font-weight: 700;
  white-space: nowrap;
}

.trend-indicator {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  margin-left: 8px;
  color: rgba(255, 255, 255, 0.45);
}

.trend-up {
  color: #4dbd74;
}

.trend-down {
  color: #f86c6b;
}

.trend-same,
.trend-none {
  color: rgba(255, 255, 255, 0.55);
}

.evolution-anomaly-row td {
  background: #181c20;
  padding-top: 0;
}

.evolution-anomaly-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 8px 0 4px 0;
}

.evolution-anomaly {
  display: flex;
  align-items: center;
  gap: 8px;
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.82rem;
}

.anomaly-category {
  min-width: 46px;
  color: #8bdcf4;
  font-weight: 800;
  text-transform: uppercase;
  font-size: 0.68rem;
}

.anomaly-name {
  color: #fff;
  font-weight: 700;
  min-width: 120px;
}

.anomaly-values {
  white-space: nowrap;
}

.anomaly-arrow {
  margin: 0 8px;
  color: rgba(255, 255, 255, 0.45);
}

.loading-overlay {
  position: absolute;
  inset: 0;
  background: rgba(10, 12, 15, 0.72);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 10;
}

.empty-state {
  min-height: 280px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.42);
}

@media (max-width: 768px) {
  .usage-controls,
  .usage-toolbar {
    width: 100%;
  }

  .control-block,
  .month-control,
  .search-control {
    width: 100%;
  }

  .usage-table-wrapper {
    max-height: none;
  }
}
</style>

<style>
.pvp-evolution-modal .modal-dialog {
  max-width: 1500px;
  width: calc(100vw - 30px);
}
</style>
